import * as React from 'react';
import type { StudyPageInfoBoxProps } from '../StudyPageInfoBox/StudyPageInfoBox';
import { StudyPageInfoBox } from '../StudyPageInfoBox/StudyPageInfoBox';
import { AlertSectionVariant } from '~/Blocks/AlertSectionBlock/AlertSectionVariant.csharp';
import type { ICampusApplicationProcessState } from '~/Features/CampusApplication/CampusApplicationProcessState';
import { CampusApplicationProcessState } from '~/Features/CampusApplication/CampusApplicationProcessState';
import { areAllItemsAdded, isItemAdded } from '~/Features/CampusApplication/Utils/campusApplicationUtils';
import type { ApplicationChoiceDto } from '~/Features/Search/ApplicationChoice/Controller/ApplicationChoiceDto.csharp';
import { Link } from '~/Partials/Link/Link';
import { LinkVariant } from '~/Partials/Link/LinkVariant.csharp';

interface StudyPageAdmissionInfoBoxTranslations {
    basketLimitHeading: string;
    basketLimitBody: string;
    ongoingOnlineHeading: string;
    ongoingOnlineBody: string;
    studyAlreadyAddedHeading: string;
    see: string;
    yourApplication: string;
}

export interface StudyPageAdmissionInfoBoxProps {
    editorialInfo: StudyPageInfoBoxProps;
    translations: StudyPageAdmissionInfoBoxTranslations;
    campusApplicationUrl: string;
    choices?: ApplicationChoiceDto[];
}

export const StudyPageAdmissionInfoBox: React.FC<StudyPageAdmissionInfoBoxProps> = props => {
    const { editorialInfo, choices, translations, campusApplicationUrl } = props;

    const [processState, setProcessState] = React.useState<ICampusApplicationProcessState | null>(null);

    React.useEffect(() => {
        setProcessState(CampusApplicationProcessState());
    }, []);

    const mapModelToProps = () => {
        if (processState) {
            if (processState.isOnlineApplicationInProgress()) {
                return {
                    header: translations.ongoingOnlineHeading,
                    body: translations.ongoingOnlineBody,
                    variant: AlertSectionVariant.Error,
                };
            }

            const isProductMultiVariant = choices && choices.length > 1;
            const isProductSingleVariant = choices && choices.length === 1;

            const currentChoices = processState.getAll();

            const areAllVariantsAdded =
                (isProductMultiVariant && areAllItemsAdded(choices, currentChoices)) ||
                (isProductSingleVariant && isItemAdded(choices[0].id, currentChoices));

            if (areAllVariantsAdded) {
                return {
                    header: translations.studyAlreadyAddedHeading,
                    body: (
                        <>
                            {`${translations.see} `}
                            <Link
                                href={campusApplicationUrl}
                                label={translations.yourApplication}
                                variant={LinkVariant.LightBlack}
                            />
                        </>
                    ),
                    variant: AlertSectionVariant.Info,
                };
            }

            if (processState.isItemsLimitReached()) {
                return {
                    header: translations.basketLimitHeading,
                    body: translations.basketLimitBody,
                    variant: AlertSectionVariant.Error,
                };
            }
        }

        if (editorialInfo.header && editorialInfo.body) {
            return editorialInfo;
        }

        return undefined;
    };

    const infoProps = mapModelToProps();

    return infoProps ? <StudyPageInfoBox {...infoProps} /> : null;
};
